import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { Crown, Check, Zap, Brain, BarChart3, Trophy, Loader2, Sparkles, XCircle, Shield, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function SubscriptionPage() {
  const { getAuthHeader, API } = useAuth();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  useEffect(() => {
    fetchData();
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/status`, { headers: getAuthHeader() })
      ]);
      setPlans(plansRes.data);
      setPremiumStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(
          `${API}/subscription/checkout/status/${sessionId}`,
          { headers: getAuthHeader() }
        );
        
        if (response.data.payment_status === 'paid') {
          toast.success('🎉 Welcome to Premium! Your subscription is now active.');
          fetchData();
          setCheckingPayment(false);
          window.history.replaceState({}, '', '/subscription');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setCheckingPayment(false);
          toast.error('Payment verification timed out. Please refresh the page.');
        }
      } catch (error) {
        setCheckingPayment(false);
        toast.error('Failed to verify payment status');
      }
    };
    
    poll();
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { plan: planId, origin_url: window.location.origin },
        { headers: getAuthHeader() }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setProcessingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { headers: getAuthHeader() });
      toast.success("Subscription cancelled. You'll keep access until your billing period ends.");
      setShowCancelDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgradeToYearly = async () => {
    setUpgrading(true);
    try {
      const response = await axios.post(`${API}/subscription/upgrade`, {}, { headers: getAuthHeader() });
      toast.success('🎉 Successfully upgraded to yearly plan!');
      setShowUpgradeDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade subscription');
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl mx-auto" data-testid="subscription-page">
        {checkingPayment && (
          <Card className="glass neon-border">
            <CardContent className="p-6 flex items-center justify-center gap-3">
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
              <span className="text-foreground">Verifying your payment...</span>
            </CardContent>
          </Card>
        )}

        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-4">
            <Crown className="h-5 w-5 text-primary" />
            <span className="text-primary font-semibold">MindForge Premium</span>
          </div>
          <h1 className="font-serif font-bold text-3xl md:text-4xl text-foreground mb-3">
            Unlock Your Full Potential
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Get unlimited access to all features and accelerate your mental training journey.
          </p>
        </div>

        {premiumStatus && (
          <Card className={`${premiumStatus.is_premium ? 'bg-primary/10 border-primary/30' : 'bg-card/50 border-white/5'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${premiumStatus.is_premium ? 'bg-primary/20' : 'bg-muted/50'}`}>
                    {premiumStatus.is_premium ? (
                      <Crown className="h-6 w-6 text-primary" />
                    ) : (
                      <Zap className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      {premiumStatus.is_premium ? 'Premium Member' : 'Free Plan'}
                    </p>
                    {premiumStatus.is_premium ? (
                      <p className="text-sm text-muted-foreground">
                        {premiumStatus.plan === 'yearly' ? 'Yearly' : 'Monthly'} subscription
                        {premiumStatus.expires_at && (
                          <span> • Valid until {new Date(premiumStatus.expires_at).toLocaleDateString()}</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {premiumStatus.ai_messages_today}/{premiumStatus.ai_messages_limit} AI messages used today
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {premiumStatus.is_premium && (
                    <>
                      <Badge className="bg-primary/20 text-primary border-primary/30">Active</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCancelDialog(true)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="glass border-destructive/20">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <XCircle className="h-5 w-5 text-destructive" />
                Cancel Subscription?
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Are you sure you want to cancel your Premium subscription?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">If you cancel:</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-secondary" />
                  You'll keep Premium access until your billing period ends
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-secondary" />
                  You can reactivate anytime before expiration
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-destructive" />
                  After expiration, you'll lose access to premium features
                </li>
              </ul>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setShowCancelDialog(false)}>
                Keep Subscription
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes, Cancel'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Confirmation Dialog */}
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent className="glass border-primary/20">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                {i18n.language === 'no' ? 'Oppgrader til Årlig?' : 'Upgrade to Yearly?'}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {i18n.language === 'no' 
                  ? 'Du er i ferd med å oppgradere fra månedlig til årlig abonnement.' 
                  : 'You\'re about to upgrade from monthly to yearly subscription.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
                <p className="font-semibold text-foreground mb-2">
                  {i18n.language === 'no' ? 'Hva du får:' : 'What you get:'}
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Check className="h-4 w-4 text-secondary" />
                    {i18n.language === 'no' ? 'Spar $19.89 per år' : 'Save $19.89 per year'}
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Check className="h-4 w-4 text-secondary" />
                    {i18n.language === 'no' ? 'Prisen justeres automatisk' : 'Price prorated automatically'}
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Check className="h-4 w-4 text-secondary" />
                    {i18n.language === 'no' ? '12 måneders tilgang' : '12 months of access'}
                  </li>
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                {i18n.language === 'no' 
                  ? 'Resterende tid på ditt månedlige abonnement vil bli kreditert mot årlig pris.' 
                  : 'Remaining time on your monthly subscription will be credited towards yearly price.'}
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setShowUpgradeDialog(false)}>
                {i18n.language === 'no' ? 'Avbryt' : 'Cancel'}
              </Button>
              <Button
                onClick={handleUpgradeToYearly}
                disabled={upgrading}
                className="bg-primary text-primary-foreground"
                data-testid="confirm-upgrade-btn"
              >
                {upgrading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Crown className="h-4 w-4 mr-2" />
                    {i18n.language === 'no' ? 'Bekreft Oppgradering' : 'Confirm Upgrade'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade option for monthly subscribers */}
        {premiumStatus?.is_premium && premiumStatus?.plan === 'monthly' && plans && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="relative overflow-hidden border-primary/50 ring-2 ring-primary/30">
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-secondary to-primary py-2 text-center">
                <span className="text-sm font-bold text-primary-foreground">
                  {i18n.language === 'no' ? 'OPPGRADER OG SPAR!' : 'UPGRADE & SAVE!'}
                </span>
              </div>
              <CardHeader className="pt-12">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl">{i18n.language === 'no' ? 'Premium Årlig' : 'Premium Yearly'}</CardTitle>
                  <Badge className="bg-secondary/20 text-secondary border-secondary/30">{i18n.language === 'no' ? 'Spar 33%' : 'Save 33%'}</Badge>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-serif text-4xl font-bold text-foreground">$39.99</span>
                  <span className="text-muted-foreground">/{i18n.language === 'no' ? 'år' : 'year'}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {i18n.language === 'no' 
                    ? 'Du betaler for tiden $4.99/måned ($59.88/år). Oppgrader til årlig og spar $19.89!' 
                    : 'You\'re currently paying $4.99/month ($59.88/year). Upgrade to yearly and save $19.89!'}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-secondary shrink-0" />
                    <span className="text-secondary font-semibold">{i18n.language === 'no' ? '7 dager GRATIS prøveperiode' : '7 days FREE trial'}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-secondary shrink-0" />
                    <span className="text-muted-foreground">{i18n.language === 'no' ? 'Alt i månedlig inkludert' : 'Everything in Monthly included'}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-secondary shrink-0" />
                    <span className="text-muted-foreground">{i18n.language === 'no' ? 'Spar $19.89 per år' : 'Save $19.89 per year'}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-secondary shrink-0" />
                    <span className="text-muted-foreground">{i18n.language === 'no' ? 'Eksklusivt årlig medlemsmerke' : 'Exclusive yearly member badge'}</span>
                  </li>
                </ul>
                <Button
                  onClick={() => setShowUpgradeDialog(true)}
                  disabled={upgrading}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="upgrade-to-yearly"
                >
                  {upgrading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Crown className="h-4 w-4 mr-2" />
                      {i18n.language === 'no' ? 'Oppgrader til Årlig' : 'Upgrade to Yearly'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {!premiumStatus?.is_premium && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="relative overflow-hidden border-white/10 h-full">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">
                    {i18n.language === 'no' ? 'Premium Månedlig' : 'Premium Monthly'}
                  </CardTitle>
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-4xl font-bold text-foreground">$4.99</span>
                    <span className="text-muted-foreground">/{i18n.language === 'no' ? 'måned' : 'month'}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {i18n.language === 'no' 
                      ? 'Perfekt for å prøve premium-funksjoner' 
                      : 'Perfect for trying premium features'}
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Ubegrensede AI Coach-meldinger' : 'Unlimited AI Coach messages'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Alle 8 meditasjonsøvelser' : 'All 8 meditation exercises'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Full analysedashbord' : 'Full analytics dashboard'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Prestasjonssporing' : 'Achievement tracking'}</span>
                    </li>
                  </ul>
                  
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <stripe-buy-button
                      buy-button-id="buy_btn_1TeLBJ42dk9qmT6UV0usZSuZ"
                      publishable-key="pk_live_51SmDJl42dk9qmT6UIME0qCOL2LtLnvXUfiZpCuBzMY8NqljTeJ1awD2WiouhsVzDTfmyPNSwYrBjyVBYf3BWQQy700g8wIcUcY"
                    >
                    </stripe-buy-button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Yearly Plan - Highlighted */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="relative overflow-hidden border-primary/50 ring-2 ring-primary/30 h-full">
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-secondary to-primary py-2 text-center">
                  <span className="text-sm font-bold text-primary-foreground">
                    {i18n.language === 'no' ? 'BESTE VERDI - SPAR 33%' : 'BEST VALUE - SAVE 33%'}
                  </span>
                </div>
                <CardHeader className="pt-12">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-serif text-xl">
                      {i18n.language === 'no' ? 'Premium Årlig' : 'Premium Yearly'}
                    </CardTitle>
                    <Badge className="bg-secondary/20 text-secondary border-secondary/30">
                      {i18n.language === 'no' ? 'Populær' : 'Popular'}
                    </Badge>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-4xl font-bold text-foreground">$39.99</span>
                    <span className="text-muted-foreground">/{i18n.language === 'no' ? 'år' : 'year'}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {i18n.language === 'no' 
                      ? 'Bare $3.33/måned - spar $19.89 per år!' 
                      : 'Just $3.33/month - save $19.89 per year!'}
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Ubegrensede AI Coach-meldinger' : 'Unlimited AI Coach messages'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Alle 8 meditasjonsøvelser' : 'All 8 meditation exercises'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Full analysedashbord' : 'Full analytics dashboard'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-muted-foreground">{i18n.language === 'no' ? 'Prestasjonssporing' : 'Achievement tracking'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-secondary shrink-0" />
                      <span className="text-secondary font-semibold">{i18n.language === 'no' ? 'Prioritert støtte' : 'Priority support'}</span>
                    </li>
                  </ul>
                  
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <stripe-buy-button
                      buy-button-id="buy_btn_1TeLTY42dk9qmT6UkWt4k5D8"
                      publishable-key="pk_live_51SmDJl42dk9qmT6UIME0qCOL2LtLnvXUfiZpCuBzMY8NqljTeJ1awD2WiouhsVzDTfmyPNSwYrBjyVBYf3BWQQy700g8wIcUcY"
                    >
                    </stripe-buy-button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Trust Badges */}
        {!premiumStatus?.is_premium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            {/* No Commitment Badge */}
            <div className="text-center mb-6">
              <p className="text-lg font-semibold text-foreground">
                {i18n.language === 'no' ? 'Ingen binding. Avbryt når som helst.' : 'No Commitment. Cancel Anytime.'}
              </p>
            </div>

            {/* Security Badge */}
            <Card className="bg-card/30 border-white/5">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-full bg-secondary/20">
                      <Shield className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        {i18n.language === 'no' ? 'Sikker Betaling' : 'Secure Payment'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'no' ? '256-bit SSL-kryptering' : '256-bit SSL encryption'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="hidden md:block w-px h-12 bg-border" />
                  
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-full bg-primary/20">
                      <CreditCard className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        {i18n.language === 'no' ? 'Drevet av Stripe' : 'Powered by Stripe'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'no' 
                          ? 'Vi lagrer aldri dine kortopplysninger' 
                          : 'We never store your card details'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground text-center mt-4">
                  {i18n.language === 'no' 
                    ? 'Alle betalinger behandles sikkert gjennom Stripe. Ditt kredittkortnummer og betalingsdata er fullstendig beskyttet og deles aldri med oss.' 
                    : 'All payments are securely processed through Stripe. Your credit card number and payment data are fully protected and never shared with us.'}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {plans && !premiumStatus?.is_premium && (
          <Card className="bg-card/30 border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-lg">{i18n.language === 'no' ? 'Gratis Plan' : 'Free Plan'}</CardTitle>
              <Link to="/auth">
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  data-testid="activate-free-btn"
                >
                  {i18n.language === 'no' ? 'Aktiver Gratis' : 'Activate Free'}
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <ul className="grid sm:grid-cols-2 gap-3">
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground shrink-0" />
                  {i18n.language === 'no' ? '1 meditasjonsøkt' : '1 meditation session'}
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground shrink-0" />
                  {i18n.language === 'no' ? '1 AI Coach-melding' : '1 AI Coach message'}
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground shrink-0" />
                  {i18n.language === 'no' ? 'Ubegrensede mål og dagbok' : 'Unlimited goals & journal'}
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground shrink-0" />
                  {i18n.language === 'no' ? 'Daglige affirmasjoner' : 'Daily affirmations'}
                </li>
              </ul>
            </CardContent>
          </Card>
        )}

        <div>
          <h2 className="font-serif text-xl text-foreground mb-4 text-center">{i18n.language === 'no' ? 'Premium Låser Opp' : 'Premium Unlocks'}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Brain, title: i18n.language === 'no' ? "Alle Meditasjoner" : "All Meditations", desc: i18n.language === 'no' ? "8 guidede øvelser" : "8 guided exercises" },
              { icon: Zap, title: i18n.language === 'no' ? "Ubegrenset AI Coach" : "Unlimited AI Coach", desc: i18n.language === 'no' ? "Ingen grenser" : "No daily limits" },
              { icon: BarChart3, title: i18n.language === 'no' ? "Full Analyse" : "Full Analytics", desc: i18n.language === 'no' ? "Spor fremgangen" : "Track your progress" },
              { icon: Trophy, title: i18n.language === 'no' ? "Prestasjoner" : "Achievements", desc: i18n.language === 'no' ? "Lås opp alle merker" : "Unlock all badges" }
            ].map((item, i) => (
              <Card key={i} className="bg-card/50 border-white/5">
                <CardContent className="p-4 text-center">
                  <item.icon className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground text-sm">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
